import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import RoofMap from "@/components/RoofMap";
import EstimateBreakdown from "@/components/EstimateBreakdown";
import ChoixMateriaux from "@/components/ChoixMateriaux";
import { money, PITCH_OPTIONS, PITCH_VIDE, pitchDefinie, computeEstimateBatiments, footprintFromPolygon, estToitPlat, paireLangues } from "@/lib/roof";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, Loader2, MapPin, User, Mail, Pencil, Save, X, Move, Plus, Home, Copy, Box, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 9);
const DEFAULT_RECT = [[0.4, 0.4], [0.6, 0.4], [0.6, 0.6], [0.4, 0.6]];
// Paire de langues du pays du compte (ca : fr/en, us : en/es), comme partout.
const NOMS_LANGUES = { fr: "Français", en: "English", es: "Español" };
// Aucun multiplicateur enregistre : la pente est INCONNUE, pas 6/12. Le
// couvreur doit la choisir avant de pouvoir reenregistrer la soumission.
const pitchByMultiplier = (m) =>
  (m ? PITCH_OPTIONS.find((p) => Math.abs(p.value - m) < 0.002) : null) || PITCH_VIDE;

export default function EstimateView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [est, setEst] = useState(null);
  const [error, setError] = useState(false);

  const [editing, setEditing] = useState(false);
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [quoteLang, setQuoteLang] = useState("en");
  const [quoteStyle, setQuoteStyle] = useState("detaillee");
  const [choix, setChoix] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/estimates/${id}`).then(({ data }) => setEst(data)).catch(() => setError(true));
  }, [id]);

  // Mesure Sat Premium PAR STRUCTURE, comme à la création : quand le contour de
  // la section active se stabilise, on réinterroge Google au centre de ce
  // contour. L'appel est gratuit (aucun quota d'analyse consommé), ce qui permet
  // de retrouver la mesure satellite même après avoir déplacé le cadrage.
  // Déclaré avant les retours anticipés : un hook doit s'exécuter à chaque rendu.
  const sectionActive = sections.find((sec) => sec.id === activeId);
  useEffect(() => {
    if (!editing || !est || !sectionActive) return;
    const pts = sectionActive.polygon || [];
    if (pts.length < 3) return;
    // Le contour n'a pas bougé depuis l'enregistrement : la mesure du document
    // fait foi. Sans ce garde-fou, ouvrir une soumission la remplaçait par une
    // mesure prise au centre du contour — et un contour ne sert qu'à désigner
    // le bâtiment, pas à le viser au mètre près.
    if (JSON.stringify(pts) === sectionActive.polygonSolar) return;
    const tm = setTimeout(async () => {
      try {
        const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
        const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
        const lat = est.lat - ((cy - 0.5) * est.ground_height_m) / 111320;
        const lng = est.lng + ((cx - 0.5) * est.ground_width_m) / (111320 * Math.cos((est.lat * Math.PI) / 180));
        const { data } = await api.post("/solar-at-point", { lat, lng });
        if (!data || !data.disponible) return;
        const bat = data.centre && data.centre.lat != null ? `${data.centre.lat},${data.centre.lng}` : "";
        setSections((list) => list.map((sec) => (sec.id === activeId
          ? { ...sec, solar: data, solarBat: bat, polygonSolar: JSON.stringify(pts),
              // Nouveau bâtiment mesuré : les pans écartés visaient l'ancien.
              segExclus: [] }
          : sec)));
      } catch { /* hors couverture ou réseau : on garde le tracé */ }
    }, 1200);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, activeId, JSON.stringify(sectionActive && sectionActive.polygon)]);

  const buildSections = (e) => {
    const raw = e.sections && e.sections.length
      ? e.sections
      : [{ label: e.roof_type || "Roof", polygon: e.polygon, footprint_sqft: e.footprint_sqft, pitch_multiplier: e.pitch_multiplier, roof_type: e.roof_type }];
    return raw.map((s) => ({
      id: uid(), label: s.label, polygon: (s.polygon || []).map((p) => [...p]),
      footprint: s.footprint_sqft || 0, pitch: pitchByMultiplier(s.pitch_multiplier), roofType: s.roof_type || "",
      // Contour d'origine : permet de revenir au tracé enregistré après avoir
      // déplacé des points sans avoir à recharger la page.
      polygonOrigine: (s.polygon || []).map((p) => [...p]),
      footprintOrigine: s.footprint_sqft || 0,
      // La soumission retient si la mesure Sat Premium avait été utilisée ; on
      // rétablit ce choix ET la mesure elle-même.
      //
      // Elle était perdue ici : seul `useSolar` revenait, et la mesure était
      // rachetée à Google au premier rendu. D'où les deux symptômes vus en
      // ouvrant une soumission — les rectangles des bâtiments et des pans
      // disparaissaient de la photo, et la surface repartait du contour tracé
      // (qui ne fait que DÉSIGNER le bâtiment) au lieu du relevé satellite.
      solar: s.solar || undefined,
      segExclus: s.seg_exclus || [],
      solarBat: s.solar_bat || "",
      solarEcart: s.solar_ecart ?? undefined,
      solarHorsBoite: !!s.solar_hors_boite,
      // Contour pour lequel la mesure ci-dessus vaut : tant qu'il n'a pas
      // bougé, rien à redemander au satellite.
      polygonSolar: JSON.stringify(s.polygon || []),
      useSolar: s.mesure === "solar",
      useSolarTouched: true,
      // Bâtiment saisi aux dimensions et/ou au forfait : sans ces champs,
      // rouvrir la soumission en recalculerait le prix sans le forfait.
      saisie: s.saisie || "",
      longueur: s.longueur || 0,
      largeur: s.largeur || 0,
      forfait: s.forfait || 0,
      coutForfait: s.cout_forfait || 0,
    }));
  };

  const startEdit = () => {
    const secs = buildSections(est);
    setSections(secs);
    setActiveId(secs[0].id);
    setClientName(est.client_name || "");
    setClientEmail(est.client_email || "");
    setQuoteLang(est.quote_language || "en");
    // Le style n'était pas repris à l'édition : le PUT le remettait donc à vide
    // et le PDF retombait sur le réglage du compte. Une soumission forfaitaire
    // corrigée repartait chez le client avec le détail des prix — exactement ce
    // que le forfait sert à cacher.
    setQuoteStyle(est.quote_style || est.settings_snapshot?.quote_style || "detaillee");
    setChoix((est.choix || []).map((c) => ({ ...c })));
    setEditing(true);
  };

  if (error) return <Layout><div className="p-10 text-ink-soft">{t("estView.notFound")}</div></Layout>;
  if (!est) return <Layout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-ink" /></div></Layout>;

  const settings = est.settings_snapshot || { waste_percent: 0, tax_percent: 0, materials: [] };
  const pdfUrl = `${api.defaults.baseURL}/estimates/${est.estimate_id}/pdf`;
  const field = "bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500";

  // ---------- READ-ONLY VIEW ----------
  const roCalc = {
    roofArea: est.roof_area_sqft, totalSquares: est.total_squares, adjustedSquares: est.adjusted_squares,
    lineItems: est.line_items || [], materialsCost: est.materials_cost, laborCost: est.labor_cost,
    subtotal: est.subtotal, tax: est.tax, total: est.total,
  };
  const hasSections = est.sections && est.sections.length > 0;
  const roSectionsBD = hasSections
    ? est.sections.map((s) => ({ label: s.label, footprintSqft: s.footprint_sqft, pitchLabel: s.pitch_label, roofArea: s.roof_area_sqft }))
    : null;
  const roExtraPolys = hasSections && est.sections.length > 1 ? est.sections.slice(1).map((s) => ({ polygon: s.polygon })) : [];

  // Repères Sat Premium : le rectangle de chaque bâtiment mesuré, et les pans
  // du bâtiment courant. C'est ce que le couvreur a sous les yeux dans
  // l'atelier de mesure ; la soumission montrait la même photo sans eux, comme
  // si le relevé satellite avait disparu au passage.
  const versFraction = (lat, lng) => [
    0.5 + ((lng - est.lng) * 111320 * Math.cos((est.lat * Math.PI) / 180)) / (est.ground_width_m || 1),
    0.5 - ((lat - est.lat) * 111320) / (est.ground_height_m || 1),
  ];
  const boiteEnPolygone = (b) => {
    const [x1, y1] = versFraction(b.nord, b.ouest);
    const [x2, y2] = versFraction(b.sud, b.est);
    return [[x1, y1], [x2, y1], [x2, y2], [x1, y2]];
  };
  const reperesSolar = (secs, courantId) => {
    if (est.lat == null || !est.ground_width_m) return [];
    const out = [];
    secs.forEach((sec) => {
      const sol = sec.solar;
      if (!sol?.boite || !(sec.useSolar ?? sec.mesure === "solar")) return;
      out.push({ boite: boiteEnPolygone(sol.boite), principal: sec.id === courantId });
      if (sec.id !== courantId) return;
      (sol.segments || [])
        .filter((seg) => seg.boite && !(sec.segExclus || sec.seg_exclus || []).includes(seg.id))
        .forEach((seg) => out.push({ boite: boiteEnPolygone(seg.boite), principal: false }));
    });
    return out;
  };
  // En lecture, le premier bâtiment porte les pans : c'est celui que la photo
  // cadre. En édition, c'est celui que le couvreur a sélectionné.
  const roReperes = hasSections
    ? reperesSolar(est.sections.map((s, i) => ({ ...s, id: i })), 0)
    : [];

  const s = est.settings_snapshot || {};
  const companyLines = [s.company_name, s.company_phone, s.company_email, s.company_website].filter(Boolean).join("\n");
  const emailToClient = () => {
    const to = est.client_email || "";
    const subject = `Roofing estimate — ${est.property_address}`;
    const body =
      `Hi ${est.client_name || "there"},\n\nPlease find attached our roofing estimate for ${est.property_address}.\n\n` +
      `Estimate total: ${money(est.total)}\nRoof area: ${Math.round(est.roof_area_sqft).toLocaleString()} sq ft (${(est.total_squares || 0).toFixed(1)} squares)\n\n` +
      `The PDF quote is attached.\n\nThank you,\n${companyLines || "Your roofer"}`;
    window.open(pdfUrl, "_blank");
    const a = document.createElement("a");
    a.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    a.style.display = "none";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success(t("estView.emailToast"));
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/estimates/${est.estimate_id}/duplicate`);
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      toast.success(t("dash.duplicated"));
      navigate(`/estimate/${data.estimate_id}`);
    } catch {
      toast.error(t("dash.duplicateFail"));
    }
  };

  // Vue 3D Google Maps en pop-up : incliner la carte montre tout de suite la pente.
  const ouvrirPente3D = () => {
    if (!est || est.lat == null || est.lng == null) return;
    const url = `https://www.google.com/maps/@${est.lat},${est.lng},70m/data=!3m1!1e3`;
    const w = Math.min(1200, window.screen.availWidth - 40);
    const h = Math.min(1200, window.screen.availHeight - 60);
    window.open(url, "pente3d", `width=${w},height=${h},left=60,top=40,noopener`);
  };

  // ---------- EDIT MODE state derived ----------
  const active = sections.find((sec) => sec.id === activeId);
  const handlePolygonChange = (next) => {
    setSections((list) => list.map((sec) =>
      sec.id === activeId ? { ...sec, polygon: next, footprint: footprintFromPolygon(next, est.ground_width_m, est.ground_height_m) || sec.footprint } : sec
    ));
  };
  const updateActive = (patch) => setSections((list) => list.map((sec) => (sec.id === activeId ? { ...sec, ...patch } : sec)));
  const addRoof = () => {
    const id2 = uid();
    const footprint = footprintFromPolygon(DEFAULT_RECT, est.ground_width_m, est.ground_height_m);
    setSections((list) => [...list, {
      id: id2, label: list.length === 1 ? t("newEst.cabananLabel") : `${t("newEst.roofN")} ${list.length + 1}`,
      polygon: DEFAULT_RECT.map((p) => [...p]), footprint, pitch: PITCH_VIDE, roofType: "structure",
      polygonOrigine: DEFAULT_RECT.map((p) => [...p]), footprintOrigine: footprint,
    }]);
    setActiveId(id2);
  };
  const removeSection = (secId) => {
    if (sections.length <= 1) return;
    const next = sections.filter((sec) => sec.id !== secId);
    setSections(next);
    if (activeId === secId) setActiveId(next[0].id);
  };

  // Surface d'une section : la mesure Sat Premium (pente déjà incluse) quand
  // elle est retenue, sinon le polygone tracé multiplié par la pente.
  const aireSection = (sec) => {
    if (sec.saisie === "dimensions") {
      return (Number(sec.longueur) || 0) * (Number(sec.largeur) || 0) * sec.pitch.value;
    }
    if (sec.useSolar && sec.solar?.disponible) {
      // Les pans écartés le restent : sans ça, rouvrir une soumission dont le
      // couvreur avait décoché la véranda en regonflait le total.
      const tous = sec.solar.segments || [];
      // Aucun détail par pan (vieille mesure, ou réponse globale) : total brut.
      if (!tous.length) return Number(sec.solar.surface_toit_pi2) || 0;
      return tous
        .filter((seg) => !(sec.segExclus || []).includes(seg.id))
        .reduce((a, seg) => a + (Number(seg.surface_toit_pi2) || 0), 0);
    }
    return sec.footprint * sec.pitch.value;
  };

  // Revenir au cadrage enregistré dans la soumission.
  const restaurerContour = () => {
    if (!active) return;
    updateActive({ polygon: (active.polygonOrigine || []).map((p) => [...p]), footprint: active.footprintOrigine || active.footprint });
    toast.info(t("newEst.resetOutlineDone"));
  };

  const roofAreaTotal = sections.reduce((a, sec) => a + aireSection(sec), 0);
  const totalFootprint = sections.reduce((a, sec) => a + sec.footprint, 0);
  // Comme à la création : le bâtiment principal décide du type de toit.
  // Même chiffrage par bâtiment qu'à la création : sans ça, rouvrir une
  // soumission mixte en changerait le prix.
  const batiments = sections.map((sec) => ({
    roofAreaSqft: aireSection(sec), plat: estToitPlat(sec.pitch?.value),
    label: sec.label,
    forfait: Number(sec.forfait) || 0,
    coutForfait: Number(sec.coutForfait) || 0,
  }));
  const platsPresents = [...new Set(batiments.map((b) => b.plat))];
  // Aucune soumission ne se réenregistre avec un bâtiment sans pente.
  const sansPente = sections.filter((sec) => !pitchDefinie(sec.pitch));
  const calc = editing ? computeEstimateBatiments({ batiments, settings, choix, lang: quoteLang }) : null;
  const editSectionsBD = sections.map((sec) => ({
    label: sec.label, footprintSqft: sec.footprint,
    pitchLabel: sec.useSolar && sec.solar?.disponible ? `${sec.pitch.label} · Sat Premium` : sec.pitch.label,
    roofArea: aireSection(sec),
  }));

  const saveEdit = async () => {
    if (sansPente.length) { toast.error(t("newEst.pitchBlock")); return; }
    setSaving(true);
    try {
      const sectionsPayload = sections.map((sec) => ({
        label: sec.label, polygon: sec.polygon, footprint_sqft: sec.footprint,
        pitch_label: sec.pitch.label, pitch_multiplier: sec.pitch.value,
        roof_area_sqft: aireSection(sec), roof_type: sec.roofType || "",
        mesure: sec.useSolar && sec.solar?.disponible ? "solar" : "trace",
        // Le relevé satellite repart avec la soumission, comme à la création.
        // Sans lui, un simple changement de nom de client effaçait les pans et
        // le rectangle du bâtiment : la soumission suivante devait racheter la
        // mesure, et le contour tracé reprenait la main sur le calcul.
        solar: sec.solar?.disponible ? sec.solar : undefined,
        seg_exclus: sec.segExclus || [],
        solar_bat: sec.solarBat || "",
        solar_ecart: sec.solarEcart ?? null,
        solar_hors_boite: !!sec.solarHorsBoite,
        // Sans ces champs, réenregistrer une soumission effacerait le forfait
        // du cabanon et la ferait retomber au calcul au carré.
        saisie: sec.saisie || "",
        longueur: Number(sec.longueur) || 0,
        largeur: Number(sec.largeur) || 0,
        forfait: Number(sec.forfait) || 0,
        cout_forfait: Number(sec.coutForfait) || 0,
      }));
      const payload = {
        property_address: est.property_address, client_name: clientName, client_email: clientEmail, quote_language: quoteLang,
        // Pas de réenregistrement de l'image : le serveur la refait à la lecture
        // si elle manque, et la renvoyer ici la réinstallerait en base.
        lat: est.lat, lng: est.lng, provider: est.provider,
        polygon: sections[0].polygon, ground_width_m: est.ground_width_m, ground_height_m: est.ground_height_m,
        footprint_sqft: totalFootprint, roof_type: sections[0].roofType || est.roof_type,
        pitch_label: sections.length > 1 ? `${sections.length} buildings` : sections[0].pitch.label,
        pitch_multiplier: sections[0].pitch.value, confidence: est.confidence, ai_notes: est.ai_notes,
        roof_area_sqft: calc.roofArea, total_squares: calc.totalSquares, adjusted_squares: calc.adjustedSquares,
        materials_cost: calc.materialsCost, labor_cost: calc.laborCost, subtotal: calc.subtotal,
        tax: calc.tax, total: calc.total, settings_snapshot: settings, line_items: calc.lineItems,
        choix, sections: sectionsPayload, quote_style: quoteStyle,
        // Le PUT remplace le document entier : sans ces deux champs, éditer une
        // soumission ferait disparaître le vendeur assigné à la création.
        vendeur_nom: est.vendeur_nom || "", vendeur_telephone: est.vendeur_telephone || "",
      };
      const { data } = await api.put(`/estimates/${est.estimate_id}`, payload);
      // La réponse du PUT relit la base telle quelle : elle ne porte donc PAS
      // l'image, que seule la lecture régénère. La reprendre à l'écran évite de
      // voir la photo disparaître au moment même où on enregistre — et épargne
      // un appel satellite pour une image qu'on a déjà sous les yeux.
      setEst({ ...data, image_base64: data.image_base64 || est.image_base64 || "" });
      setEditing(false);
      // Le tableau de bord affiche le total : sans invalidation il resterait
      // sur l'ancien montant tant que sa liste est en cache.
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      toast.success(t("estView.updated"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <button onClick={() => navigate("/dashboard")} data-testid="back-button" className="flex items-center gap-2 text-sm text-ink-soft hover:text-ink mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> {t("estView.back")}
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">{t("estView.overline")}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tighter flex items-center gap-2">
              <MapPin className="h-6 w-6 text-brand-ink" /> {est.property_address}
            </h1>
            {!editing && est.client_name && (
              <p className="text-ink-soft text-sm mt-2 flex items-center gap-2"><User className="h-4 w-4" /> {est.client_name}{est.client_email ? ` · ${est.client_email}` : ""}</p>
            )}
          </div>
          {editing ? (
            <div className="flex items-center gap-2">
              <Button onClick={() => setEditing(false)} data-testid="cancel-edit-button" variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink font-semibold">
                <X className="h-4 w-4 mr-2" /> {t("estView.cancel")}
              </Button>
              <Button onClick={saveEdit} disabled={saving || sansPente.length > 0}
                title={sansPente.length ? `${t("newEst.pitchBlock")} ${sansPente.map((sec) => sec.label).join(", ")}` : ""}
                data-testid="save-edit-button" className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> {t("estView.save")}</>}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button onClick={startEdit} data-testid="edit-estimate-button" variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink font-semibold">
                <Pencil className="h-4 w-4 mr-2" /> {t("estView.edit")}
              </Button>
              <Button onClick={duplicate} data-testid="duplicate-estimate-button" variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink font-semibold">
                <Copy className="h-4 w-4 mr-2" /> {t("estView.duplicate")}
              </Button>
              <Button onClick={emailToClient} data-testid="email-quote-button" variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink font-semibold">
                <Mail className="h-4 w-4 mr-2" /> {t("estView.emailBtn")}
              </Button>
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf-button">
                <Button className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
                  <FileText className="h-4 w-4 mr-2" /> {t("estView.downloadBtn")}
                </Button>
              </a>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7">
              <RoofMap image={est.image_base64} polygon={est.polygon} extraPolygons={roExtraPolys} guides={roReperes} className="aspect-square" />
              {est.lat != null && est.lng != null && (
                <button onClick={ouvrirPente3D} data-testid="check-pitch-3d-view" className="mt-3 flex items-center gap-1.5 text-xs text-brand-ink hover:text-brand-ink transition-colors font-semibold">
                  <Box className="h-3.5 w-3.5" /> {t("newEst.check3d")}
                </button>
              )}
            </div>
            <div className="lg:col-span-5">
              <EstimateBreakdown calc={roCalc} settings={settings} sections={roSectionsBD} footprintSqft={est.footprint_sqft} roofType={est.roof_type} pitchLabel={est.pitch_label} />
            </div>
          </div>
        ) : active && calc && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {sections.map((sec, i) => (
                  <div key={sec.id} className={`flex items-center rounded-md border transition-colors ${sec.id === activeId ? "bg-orange-600 border-orange-600 text-white" : "bg-surface border-line text-ink-soft hover:bg-hover-strong"}`}>
                    <button data-testid={`section-tab-${i}`} onClick={() => setActiveId(sec.id)} className="flex items-center gap-2 pl-3 pr-2 py-1.5 text-xs font-semibold">
                      <Home className="h-3.5 w-3.5" /> {sec.label}
                      <span className={sec.id === activeId ? "text-ink/80" : "text-ink-faint"}>· {Math.round(sec.footprint)} {t("units.sqft")}</span>
                    </button>
                    {sections.length > 1 && (
                      <button data-testid={`section-delete-${i}`} onClick={() => removeSection(sec.id)} className="px-1.5 py-1.5 opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                ))}
                <button data-testid="add-roof-button" onClick={addRoof} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-dashed border-line-strong text-ink-soft hover:border-orange-500 hover:text-brand-ink transition-colors">
                  <Plus className="h-3.5 w-3.5" /> {t("newEst.addRoof")}
                </button>
              </div>

              <RoofMap image={est.image_base64} polygon={active.polygon} editable onPolygonChange={handlePolygonChange}
                guides={reperesSolar(sections, activeId)}
                extraPolygons={sections.filter((sec) => sec.id !== activeId).map((sec) => ({ polygon: sec.polygon }))} className="aspect-square" />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-brand-ink">
                  <Move className="h-3.5 w-3.5" /> {t("newEst.dragHint")}
                </div>
                <div className="flex items-center gap-4">
                  <button onClick={restaurerContour} data-testid="reset-outline-edit" className="flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors font-semibold">
                    <RotateCcw className="h-3.5 w-3.5" /> {t("newEst.resetOutline")}
                  </button>
                  {est.lat != null && est.lng != null && (
                    <button onClick={ouvrirPente3D} data-testid="check-pitch-3d-edit" className="flex items-center gap-1.5 text-xs text-brand-ink hover:text-brand-ink transition-colors font-semibold">
                      <Box className="h-3.5 w-3.5" /> {t("newEst.check3d")}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-4">
              <div className="bg-surface border border-line rounded-xl p-6 shadow-carte space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint">{t("newEst.editing")}: {active.label}</h3>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.sectionName")}</Label>
                  <Input data-testid="section-label-input" value={active.label} onChange={(e) => updateActive({ label: e.target.value })} className={`mt-1 ${field}`} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.roofPitch")}</Label>
                  <Select value={pitchDefinie(active.pitch) ? String(active.pitch.value) : undefined}
                          onValueChange={(v) => updateActive({ pitch: PITCH_OPTIONS.find((p) => String(p.value) === v) })}>
                    <SelectTrigger data-testid="pitch-select"
                      className={`mt-1 ${field} ${pitchDefinie(active.pitch) ? "" : "border-amber-500"}`}>
                      <SelectValue placeholder={t("newEst.pitchChoose")} />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      {PITCH_OPTIONS.map((p) => (<SelectItem key={p.label} value={String(p.value)} className="focus:bg-orange-600 focus:text-white">{p.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {!pitchDefinie(active.pitch) && (
                    <p data-testid="pitch-manquante" className="text-xs text-amber-700 mt-1.5">{t("newEst.pitchRequired")}</p>
                  )}
                  {active.solar?.disponible && (
                    <div className="mt-1.5">
                      <p data-testid="section-solar-badge" className="text-xs text-brand-ink">
                        {t("newEst.solarBadge")} : {active.solar.pente_principale === "flat" ? t("newEst.solarFlat") : active.solar.pente_principale}
                        {" · "}{Number(active.solar.surface_toit_pi2).toLocaleString()} {t("units.sqft")}
                      </p>
                      <label className="flex items-center gap-2 text-xs text-ink-soft cursor-pointer mt-1.5">
                        <input
                          type="checkbox"
                          data-testid="use-solar-checkbox"
                          checked={!!active.useSolar}
                          onChange={(e) => updateActive({ useSolar: e.target.checked, useSolarTouched: true })}
                          className="accent-orange-600 h-3.5 w-3.5"
                        />
                        {t("newEst.useSolar")}
                      </label>
                      {active.useSolar && (
                        <p className="text-[11px] text-ink-faint mt-1">{t("newEst.useSolarHint")}</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.footprintTune")}</Label>
                  <Input data-testid="footprint-input" type="number" value={Math.round(active.footprint)} onChange={(e) => updateActive({ footprint: Number(e.target.value) })} className={`mt-1 ${field}`} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.clientName")}</Label>
                    <Input data-testid="client-name-input" value={clientName} onChange={(e) => setClientName(e.target.value)} className={`mt-1 ${field}`} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("newEst.clientEmail")}</Label>
                    <Input data-testid="client-email-input" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className={`mt-1 ${field}`} />
                  </div>
                </div>
                <div>
                  <ChoixMateriaux settings={settings} valeur={choix} onChange={setChoix} platsPresents={platsPresents} lang={quoteLang} />
                  <Label className="mt-4 block text-xs uppercase tracking-widest text-ink-soft">{t("newEst.quoteLang")}</Label>
                  <Select value={quoteLang} onValueChange={setQuoteLang}>
                    <SelectTrigger data-testid="estimate-quote-language-select" className={`mt-1 ${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      {paireLangues(settings?.country).map((l) => (
                        <SelectItem key={l} value={l} className="focus:bg-orange-600 focus:text-white">{NOMS_LANGUES[l]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Le type de soumission se corrige après coup : un couvreur
                      qui a chiffré en détaillé change souvent d'avis en voyant
                      le total, et refaire la soumission lui coûtait une mesure. */}
                  <Label className="mt-4 block text-xs uppercase tracking-widest text-ink-soft">{t("newEst.quoteStyle")}</Label>
                  <Select value={quoteStyle} onValueChange={setQuoteStyle}>
                    <SelectTrigger data-testid="estimate-quote-style-select" className={`mt-1 ${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      <SelectItem value="detaillee" className="focus:bg-orange-600 focus:text-white">{t("newEst.styleDetailed")}</SelectItem>
                      <SelectItem value="forfaitaire" className="focus:bg-orange-600 focus:text-white">{t("newEst.styleLump")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {quoteStyle === "forfaitaire" && (
                    <p className="text-xs text-ink-faint mt-1">{t("newEst.lumpHint")}</p>
                  )}
                </div>
              </div>

              <EstimateBreakdown calc={calc} settings={settings} sections={editSectionsBD} footprintSqft={totalFootprint} roofType={sections[0].roofType} pitchLabel={sections[0].pitch.label} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
